import dagre from 'dagre';
import { Node, Edge } from '@xyflow/react';

import type { Model } from '@/explore/types';

import type { ModelNodeData } from './ModelNode';
import type { ModelEdgeData } from './ModelEdge';

const NODE_WIDTH = 280;
const NODE_BASE_HEIGHT = 120;
const PROPERTY_HEIGHT = 24;
const MAX_VISIBLE_PROPERTIES = 10;

/**
 * Calculate the height of a model node based on its number of properties
 */
function calculateNodeHeight(model: Model): number {
  const visibleProps = Math.min(model.properties.length, MAX_VISIBLE_PROPERTIES);
  return NODE_BASE_HEIGHT + visibleProps * PROPERTY_HEIGHT;
}

/**
 * Build edges in xyflow format from models
 */
function buildEdges(models: Model[]): Edge<ModelEdgeData>[] {
  const edges: Edge<ModelEdgeData>[] = [];
  const nodeIds = new Set(models.map((m) => m.modelId));
  // Track edges we've already created to avoid duplicates
  const edgeKeys = new Set<string>();

  models.forEach((model) => {
    model.relations.forEach((relation) => {
      if (nodeIds.has(relation.modelId)) {
        // Create a normalized key to identify this edge (sort to make it bidirectional)
        const edgeKey = [model.modelId, relation.modelId].sort().join('->');

        // Skip if we've already created an edge between these two models
        if (edgeKeys.has(edgeKey)) {
          return;
        }
        edgeKeys.add(edgeKey);

        // Determine join type: if joinStrategy has 'steps' property, it's stepped, otherwise direct
        const joinType =
          relation.joinStrategy && 'steps' in relation.joinStrategy ? 'stepped' : 'direct';

        // Extract join keys from the relation's join strategy
        let joinKeyOnBase: string | undefined;
        let joinKeyOnRelated: string | undefined;
        if (relation.joinStrategy && 'joinKeyOnBase' in relation.joinStrategy) {
          joinKeyOnBase = relation.joinStrategy.joinKeyOnBase;
          joinKeyOnRelated = relation.joinStrategy.joinKeyOnRelated;
        }

        edges.push({
          id: `${model.modelId}-${relation.key}`,
          source: model.modelId,
          target: relation.modelId,
          type: 'modelEdge',
          data: {
            joinType,
            joinKeyOnBase,
            joinKeyOnRelated,
          },
        });
      }
    });
  });

  return edges;
}

/**
 * Calculate layout positions using dagre for automatic hierarchical layout
 */
export function calculateLayout(models: Model[]): {
  nodes: Node<ModelNodeData>[];
  edges: Edge<ModelEdgeData>[];
} {
  if (models.length === 0) {
    return { nodes: [], edges: [] };
  }

  const edges = buildEdges(models);

  // Create a new directed graph
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Configure the graph layout
  dagreGraph.setGraph({
    rankdir: 'LR', // Left to right layout
    nodesep: 80, // Horizontal spacing between nodes
    ranksep: 150, // Vertical spacing between ranks
    edgesep: 50, // Spacing between edges
    marginx: 50,
    marginy: 50,
  });

  // Add nodes to the graph
  models.forEach((model) => {
    const height = calculateNodeHeight(model);
    dagreGraph.setNode(model.modelId, {
      width: NODE_WIDTH,
      height,
    });
  });

  // Add edges to the graph
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate the layout
  dagre.layout(dagreGraph);

  // Convert dagre nodes to xyflow nodes
  const nodes: Node<ModelNodeData>[] = models.map((model) => {
    const nodeWithPosition = dagreGraph.node(model.modelId);
    const height = calculateNodeHeight(model);

    return {
      id: model.modelId,
      type: 'modelNode',
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - height / 2,
      },
      data: {
        model,
      },
      measured: {
        width: NODE_WIDTH,
        height,
      },
    };
  });

  return { nodes, edges };
}
